import React, { useState, useMemo } from "react";
import { Button, Card, Col, Form, Input, Row, Select, Transfer, Modal, message } from "antd";
import { MinusCircleOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import i18n from "../../../../translations/i18n";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PRODUCTS_TABLE } from "../../../../constants/FirestoreConstant";
import _ from "lodash";
import { findCategoryById, findSubcategoryById } from "../../../../functions/categories";

const { Option } = Select;

const TraceabilityField = ({ products, currentProductId, currentProductCategories, form, categories }) => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [bulkCopyModalVisible, setBulkCopyModalVisible] = useState(false);
  const [targetKeys, setTargetKeys] = useState([]);
  const [bulkCopyLoading, setBulkCopyLoading] = useState(false);

  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || !categories) return "";
    return categoryIds
      .map((catId) => {
        let category = findCategoryById(categories, catId);
        
        if (!category) {
          category = findSubcategoryById(
            categories.flatMap((cat) => cat.subcategories || []),
            catId
          );
        }
        
        return category ? category.name : catId;
      })
      .join(", ");
  };

  const relatedProducts = useMemo(() => {
    if (!products || !currentProductCategories || currentProductCategories.length === 0) {
      return [];
    }

    return products
      .filter((product) => {
        if (product.id === currentProductId) return false;
        if (!product.categories || product.categories.length === 0) return false;
        
        return _.intersection(product.categories, currentProductCategories).length > 0;
      })
      .map((product) => ({
        key: product.id,
        title: product.name,
        description: getCategoryNames(product.categories),
      }));
  }, [products, currentProductCategories, currentProductId, categories]);

  const openBulkCopyModal = () => {
    setTargetKeys([]);
    setBulkCopyModalVisible(true);
  };

  const handleBulkCopy = async () => {
    if (targetKeys.length === 0) {
      message.warning(t("select_products_for_bulk_edit"));
      return;
    }

    const traceability = form.getFieldValue("traceability");

    if (!traceability || traceability.length === 0) {
      message.error(t("traceability.complete_before_bulk_copy"));
      return;
    }

    setBulkCopyLoading(true);

    try {
      const batch = firestore.batch();

      targetKeys.forEach((productId) => {
        const productRef = firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(productId);
        batch.update(productRef, { traceability });
      });

      await batch.commit();

      message.success(t("traceability.bulk_copy_success", { count: targetKeys.length }));

      setBulkCopyModalVisible(false);
      setTargetKeys([]);
    } catch (error) {
      console.error("Bulk copy error:", error);
      message.error(t("traceability.bulk_copy_error"));
    } finally {
      setBulkCopyLoading(false);
    }
  };

  const handleTransferChange = (newTargetKeys) => {
    setTargetKeys(newTargetKeys);
  };

  const stepTypes = [
    { value: "birth", label: t("traceability.birth"), icon: "🐄" },
    { value: "breed", label: t("traceability.breed"), icon: "🧬" },
    { value: "farm", label: t("traceability.farm"), icon: "🌾" },
    { value: "feed", label: t("traceability.feed"), icon: "🌱" },
    { value: "slaughterhouse", label: t("traceability.slaughterhouse"), icon: "🏭" },
    { value: "butcher", label: t("traceability.butcher"), icon: "🔪" },
    { value: "delivery", label: t("traceability.delivery"), icon: "🚚" },
    { value: "custom", label: t("traceability.custom"), icon: "📍" },
  ];

  return (
    <Card title={t("traceability.title")}>
      <p style={{ marginBottom: 16, color: "#666" }}>
        {t("traceability.description")}
      </p>
      <Form.List name="traceability">
        {(fields, { add, remove }) => {
          return (
            <div className="m-3">
              {fields.map((field, index) => (
                <Card
                  key={field.key}
                  size="small"
                  title={`${t("traceability.step")} ${index + 1}`}
                  extra={
                    <MinusCircleOutlined
                      onClick={() => remove(field.name)}
                      style={{ color: "red", cursor: "pointer" }}
                    />
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        {...field}
                        label={t("traceability.step_type")}
                        name={[field.name, "type"]}
                        rules={[
                          {
                            required: true,
                            message: i18n.t("traceability.select_step_type"),
                          },
                        ]}
                      >
                        <Select placeholder={t("traceability.select_step_type")}>
                          {stepTypes.map((type) => (
                            <Option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...field}
                        label={t("traceability.label")}
                        name={[field.name, "label"]}
                        rules={[
                          {
                            required: true,
                            message: i18n.t("traceability.enter_label"),
                          },
                        ]}
                      >
                        <Input placeholder={t("traceability.label_placeholder")} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        {...field}
                        label={t("traceability.value")}
                        name={[field.name, "value"]}
                        rules={[
                          {
                            required: true,
                            message: i18n.t("traceability.enter_value"),
                          },
                        ]}
                      >
                        <Input placeholder={t("traceability.value_placeholder")} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  {t("traceability.add_step")}
                </Button>
              </Form.Item>
              {fields.length > 0 && relatedProducts.length > 0 && (
                <Form.Item>
                  <Button
                    onClick={openBulkCopyModal}
                    block
                    icon={<CopyOutlined />}
                  >
                    {t("traceability.copy_to_products")}
                  </Button>
                </Form.Item>
              )}
            </div>
          );
        }}
      </Form.List>

      <Modal
        title={t("traceability.copy_to_products")}
        open={bulkCopyModalVisible}
        onOk={handleBulkCopy}
        onCancel={() => setBulkCopyModalVisible(false)}
        okText={t("apply")}
        cancelText={t("cancel")}
        confirmLoading={bulkCopyLoading}
        width={700}
      >
        <p style={{ marginBottom: 16 }}>
          {t("traceability.bulk_copy_description")}
        </p>
        <Transfer
          dataSource={relatedProducts}
          titles={[t("available_products"), t("selected_products")]}
          targetKeys={targetKeys}
          onChange={handleTransferChange}
          render={(item) => (
            <div>
              <div style={{ fontWeight: 500 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: "#999" }}>{item.description}</div>
            </div>
          )}
          listStyle={{
            width: 300,
            height: 400,
          }}
          showSearch
          filterOption={(inputValue, item) =>
            item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
          }
        />
      </Modal>
    </Card>
  );
};

export default TraceabilityField;
